import { Routes, Route, Navigate } from 'react-router-dom'

import SignInPage from './pages/sign-in/SignInPage'
import OnboardingPage from './pages/onboarding/OnboardingPage'
import DashboardPage from './pages/dashboard/DashboardPage'
import DiscoverPage from './pages/discover/DiscoverPage'
import DiscoverDetailPage from './pages/discover-detail/DiscoverDetailPage'
import PublicSharePage from './pages/public-share/PublicSharePage'
import ProfilePage from './pages/profile/ProfilePage'
import SavedListingsPage from './pages/saved-listings/SavedListingsPage'
import ImportedListingDetailPage from './pages/imported-listing-detail/ImportedListingDetailPage'
import ActivityPage from './pages/activity/ActivityPage'
import LeaseCheckPage from './pages/lease-check/LeaseCheckPage'
import MovePrepPage from './pages/move-prep/MovePrepPage'
import NotFoundPage from './pages/not-found/NotFoundPage'
import PingsPage from './pages/pings/PingsPage'
import MessagesPage from './pages/messages/MessagesPage'
import SavedSearchesPage from './pages/saved-searches/SavedSearchesPage'
import SdkBridgePage from './pages/sdk-bridge/SdkBridgePage'
import ExtensionAuthPage from './pages/extension-auth/ExtensionAuthPage'
import MagicLinkCallbackPage from './pages/magic-link-callback/MagicLinkCallbackPage'
import ClaimTenancyPage from './pages/claim-tenancy/ClaimTenancyPage'
import RenewalOfferPage from './pages/renewal-offer/RenewalOfferPage'
import MyHomePage from './pages/my-home/MyHomePage'
import HelpPage from './pages/help/HelpPage'
import InquirePage from './pages/inquire/InquirePage'
import AnonClaimPage from './pages/anon-claim/AnonClaimPage'
import VerificationConnectPage from './pages/verification-connect/VerificationConnectPage'
import PaymentsPage from './pages/payments/PaymentsPage'
import PayRentPage from './pages/payments/pay/PayRentPage'
import AutopaySetupPage from './pages/payments/autopay/AutopaySetupPage'
import MaintenancePage from './pages/maintenance/MaintenancePage'
import AnnouncementsPage from './pages/announcements/AnnouncementsPage'
import DocumentsPage from './pages/documents/DocumentsPage'
import PollsPage from './pages/polls/PollsPage'
import AmenitiesPage from './pages/amenities/AmenitiesPage'
import AmenityBookingPage from './pages/amenities/AmenityBookingPage'
import CommunityPage from './pages/community/CommunityPage'
import ReceiptPage from './pages/receipt/ReceiptPage'
import AuthGate from './components/auth-gate/AuthGate'
import RouteGuard from './components/route-guard/RouteGuard'

/**
 * Top-level route table. Routes fall into a few guard categories:
 *
 *   /                  AuthGate routes to /sign-in, /onboarding, or /dashboard
 *   public-only        /sign-in (FirebaseUI); signed-in users are bounced away
 *   protected          signed-in but onboarding NOT required — /onboarding and
 *                      the resident surfaces /my-home, /payments, /maintenance
 *                      (a claimed renter need not have completed apartment-search
 *                      onboarding)
 *   onboarded-only     the apartment-search surfaces — /dashboard, /discover,
 *                      /profile, /saved-listings, /saved-searches, /messages, …
 *   raw (no guard)     self-managing pages — /claim-tenancy (reads its own auth
 *                      state), /renewal-offer (the token in the link is the
 *                      only credential), /me/:slug, /sdk-bridge, /magic-link-callback,
 *                      and the cross-site inquiry flow /inquire/:inventoryId +
 *                      its first-party popup /anon-claim (both must work for a
 *                      not-yet-signed-in renter embedded on a host site)
 *   *                  redirect to /
 *
 * The /discover BROWSE page is flagged `hideWhenEmbedded` and bounces to
 * /dashboard inside a host-site embed — Discover is QuickPass's own
 * listing-browser and should not surface in a partner's page. The
 * /discover/:listingId DETAIL page is NOT hidden: a renter must still be able
 * to open a saved listing to view it, even embedded.
 *
 * @returns {JSX.Element}
 */
const AppRoutes = (): JSX.Element => {
  return (
    <Routes>
      <Route path="/" element={<AuthGate />} />

      <Route
        path="/sign-in"
        element={
          <RouteGuard kind="public-only">
            <SignInPage />
          </RouteGuard>
        }
      />

      <Route path="/sign-up" element={<Navigate to="/sign-in" replace />} />

      <Route
        path="/onboarding"
        element={
          <RouteGuard kind="protected">
            <OnboardingPage />
          </RouteGuard>
        }
      />

      <Route
        path="/dashboard"
        element={
          <RouteGuard kind="onboarded-only">
            <DashboardPage />
          </RouteGuard>
        }
      />

      <Route
        path="/discover"
        element={
          <RouteGuard kind="onboarded-only" hideWhenEmbedded>
            <DiscoverPage />
          </RouteGuard>
        }
      />

      <Route
        path="/discover/:listingId"
        element={
          <RouteGuard kind="onboarded-only">
            <DiscoverDetailPage />
          </RouteGuard>
        }
      />

      <Route
        path="/profile"
        element={
          <RouteGuard kind="onboarded-only">
            <ProfilePage />
          </RouteGuard>
        }
      />

      <Route
        path="/saved-listings"
        element={
          <RouteGuard kind="onboarded-only">
            <SavedListingsPage />
          </RouteGuard>
        }
      />

      <Route
        path="/imported-listings/:listingId"
        element={
          <RouteGuard kind="onboarded-only">
            <ImportedListingDetailPage />
          </RouteGuard>
        }
      />

      <Route
        path="/activity"
        element={
          <RouteGuard kind="onboarded-only">
            <ActivityPage />
          </RouteGuard>
        }
      />

      <Route
        path="/move-prep"
        element={
          <RouteGuard kind="onboarded-only">
            <MovePrepPage />
          </RouteGuard>
        }
      />

      <Route
        path="/lease-check"
        element={
          <RouteGuard kind="onboarded-only">
            <LeaseCheckPage />
          </RouteGuard>
        }
      />

      <Route
        path="/pings"
        element={
          <RouteGuard kind="onboarded-only">
            <PingsPage />
          </RouteGuard>
        }
      />

      <Route
        path="/messages"
        element={
          <RouteGuard kind="onboarded-only">
            <MessagesPage />
          </RouteGuard>
        }
      />

      <Route
        path="/saved-searches"
        element={
          <RouteGuard kind="onboarded-only">
            <SavedSearchesPage />
          </RouteGuard>
        }
      />

      <Route
        path="/my-home"
        element={
          <RouteGuard kind="protected">
            <MyHomePage />
          </RouteGuard>
        }
      />

      <Route
        path="/help"
        element={
          <RouteGuard kind="protected">
            <HelpPage />
          </RouteGuard>
        }
      />

      <Route
        path="/payments"
        element={
          <RouteGuard kind="protected">
            <PaymentsPage />
          </RouteGuard>
        }
      />

      <Route
        path="/pay/:tenancyId"
        element={
          <RouteGuard kind="protected">
            <PayRentPage />
          </RouteGuard>
        }
      />

      <Route
        path="/autopay/:tenancyId"
        element={
          <RouteGuard kind="protected">
            <AutopaySetupPage />
          </RouteGuard>
        }
      />

      <Route
        path="/maintenance"
        element={
          <RouteGuard kind="protected">
            <MaintenancePage />
          </RouteGuard>
        }
      />

      <Route
        path="/announcements"
        element={
          <RouteGuard kind="protected">
            <AnnouncementsPage />
          </RouteGuard>
        }
      />

      <Route
        path="/documents"
        element={
          <RouteGuard kind="protected">
            <DocumentsPage />
          </RouteGuard>
        }
      />

      <Route
        path="/polls"
        element={
          <RouteGuard kind="protected">
            <PollsPage />
          </RouteGuard>
        }
      />

      <Route
        path="/amenities"
        element={
          <RouteGuard kind="protected">
            <AmenitiesPage />
          </RouteGuard>
        }
      />

      <Route
        path="/amenities/:amenityId"
        element={
          <RouteGuard kind="protected">
            <AmenityBookingPage />
          </RouteGuard>
        }
      />

      <Route
        path="/community"
        element={
          <RouteGuard kind="protected">
            <CommunityPage />
          </RouteGuard>
        }
      />

      <Route
        path="/receipt/:entryId"
        element={
          <RouteGuard kind="protected">
            <ReceiptPage />
          </RouteGuard>
        }
      />

      <Route path="/me/:slug" element={<PublicSharePage />} />

      <Route path="/inquire" element={<InquirePage />} />

      <Route path="/inquire/:inventoryId" element={<InquirePage />} />

      <Route path="/anon-claim" element={<AnonClaimPage />} />
      <Route
        path="/verification-connect"
        element={<VerificationConnectPage />}
      />

      <Route path="/sdk-bridge" element={<SdkBridgePage />} />

      <Route path="/extension-auth" element={<ExtensionAuthPage />} />

      <Route path="/magic-link-callback" element={<MagicLinkCallbackPage />} />

      <Route path="/claim-tenancy" element={<ClaimTenancyPage />} />

      <Route path="/renewal-offer" element={<RenewalOfferPage />} />

      <Route path="/404" element={<NotFoundPage />} />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}

export default AppRoutes
